import type { ComponentProps, FC, Ref } from 'react';
import { useState } from 'react';
import { StyleSheet, type StyleProp, type TextInput as NativeTextInput, type TextStyle } from 'react-native';
import { TextInput } from 'react-native-paper';
import { SvgIconType } from '@thermenresorts/icons';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Translations from 'src/core/utils/Translations';
import SvgIcon from 'src/presentation/components/atoms/SvgIcon';

type PaperTextInputProps = ComponentProps<typeof TextInput>;

export type PasswordFieldHandle = NativeTextInput;

export type Props = {
	label: string;
	ref?: Ref<PasswordFieldHandle>;
	value: string;
	hasError?: boolean;
	isNewPassword?: boolean;
	// Left out, the field keeps its own toggle; handed in, the caller can reset it between openings.
	isVisible?: boolean;
	onVisibilityChange?: (isVisible: boolean) => void;
	style?: StyleProp<TextStyle>;
	onChangeText: (value: string) => void;
	onKeyPress?: PaperTextInputProps['onKeyPress'];
	onPressIn?: PaperTextInputProps['onPressIn'];
	onSubmitEditing?: PaperTextInputProps['onSubmitEditing'];
};

const PasswordField: FC<Props> = ({
	label,
	ref,
	value,
	hasError = false,
	isNewPassword = false,
	isVisible,
	style,
	onChangeText,
	onVisibilityChange,
	onKeyPress,
	onPressIn,
	onSubmitEditing,
}) => {
	const [isVisibleInternally, setVisibleInternally] = useState(false);

	const visible = isVisible ?? isVisibleInternally;
	const toggle = () => (onVisibilityChange ? onVisibilityChange(!visible) : setVisibleInternally(!visible));

	const newPasswordProps = isNewPassword ? { textContentType: 'oneTimeCode' as const } : {};

	return (
		<TextInput
			accessibilityLabel={label}
			autoCapitalize="none"
			error={hasError}
			label={label}
			ref={ref}
			right={
				<TextInput.Icon
					accessibilityLabel={visible ? Translations.hidePassword : Translations.showPassword}
					icon={() => (
						<SvgIcon
							name={visible ? SvgIconType.eyeOff : SvgIconType.eye}
							size={Dimens.iconSize24}
							tintColor={Colors.gray900}
						/>
					)}
					onPress={toggle}
				/>
			}
			secureTextEntry={!visible}
			style={[styles.input, style]}
			{...newPasswordProps}
			value={value}
			onChangeText={onChangeText}
			onKeyPress={onKeyPress}
			onPressIn={onPressIn}
			onSubmitEditing={onSubmitEditing}
		/>
	);
};

const styles = StyleSheet.create({
	input: {
		backgroundColor: Colors.gray100,
		zIndex: Dimens.zIndexOverlay,
	},
});

export default PasswordField;
